import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import {
  InlineAddTable,
  InlineRow,
} from '../../../../components/inline-add-table/InlineAddTable';
import {
  TdCheckbox,
  TdDate,
  TdInput,
  TdMoney,
  TdNumber,
  TdSelect,
} from '../../../../components/inline-add-table/cells';
import type { SelectOption } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { ppmpsApi } from '../ppmpsApi';
import type { PpmpDetail, PpmpLineItemInput } from '../ppmpTypes';
import { procurementCatalogsApi, ppmpItemsApi } from '../procurementCatalogsApi';
import type {
  PpmpItemCatalogOption,
  ProcurementCatalogOption,
} from '../procurementCatalogTypes';
import { samplePpmpLine } from '../utils/samplePpmp';

/** `estimatedBudget` to `estimated-budget`, exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * The id of one inline cell — `ppmp-lines-items.2.quantity`.
 *
 * **This is a contract with the server.** `PpmpLineItemsRequestValidator` overrides
 * FluentValidation's indexer so a rejected line reads `items.2.quantity` rather than
 * `Items[2].Quantity`, and `focusValidationError` resolves that to `${idPrefix}-${kebab(field)}`.
 * Change either side and the reader is told which cell is wrong while nothing on screen moves —
 * which reads as the feature not being wired at all.
 */
const cellId = (index: number, column: string) => `ppmp-lines-items.${index}.${kebab(column)}`;

interface DraftRow {
  key: string;
  ppmpItemCatalogId: string | null;
  /**
   * The catalogue entry's own MARKUP, kept only while the reader has not retyped the cell.
   *
   * A specification is routinely a table of dimensions, and flattening it to the plain text the
   * grid can show would throw that away on every batch add. So the row carries both: the HTML
   * is what is SENT while it survives, and typing in the description cell drops it, because
   * from then on the typed words are what the line says.
   */
  descriptionHtml: string | null;
  descriptionText: string;
  procurementTypeId: string | null;
  modeOfProcurementId: string | null;
  quantity: number | null;
  unit: string;
  estimatedBudget: number | null;
  preProcurementConference: boolean;
  procurementStartDate: string;
  procurementEndDate: string;
  expectedDeliveryDate: string;
  remarks: string;
}

const EMPTY = (): DraftRow => ({
  key: uid('line'),
  ppmpItemCatalogId: null,
  descriptionHtml: null,
  descriptionText: '',
  procurementTypeId: null,
  modeOfProcurementId: null,
  quantity: null,
  unit: '',
  estimatedBudget: null,
  preProcurementConference: false,
  procurementStartDate: '',
  procurementEndDate: '',
  expectedDeliveryDate: '',
  remarks: '',
});

/**
 * Is this row filled in enough to count?
 *
 * The REQUIRED columns and nothing else. A row carrying none of them is one the reader pressed
 * `+` on and abandoned, and the server DROPS it rather than refusing the batch — which is why
 * the per-line validator skips a blank row too, or the drop would be unreachable.
 */
function isFilled(row: DraftRow): boolean {
  return (
    row.descriptionText.trim().length > 0 &&
    row.procurementTypeId !== null &&
    row.modeOfProcurementId !== null &&
    row.unit.trim().length > 0 &&
    row.quantity !== null &&
    row.quantity > 0
  );
}

/** Nothing typed at all — the shape the server's own blank check tests. */
function isBlank(row: DraftRow): boolean {
  return (
    row.descriptionText.trim().length === 0 &&
    row.ppmpItemCatalogId === null &&
    row.procurementTypeId === null &&
    row.modeOfProcurementId === null &&
    row.unit.trim().length === 0 &&
    (row.quantity ?? 0) === 0 &&
    (row.estimatedBudget ?? 0) === 0 &&
    !row.preProcurementConference &&
    row.procurementStartDate.length === 0 &&
    row.procurementEndDate.length === 0 &&
    row.expectedDeliveryDate.length === 0 &&
    row.remarks.trim().length === 0
  );
}

/**
 * Add a BATCH of procurement lines to one PPMP.
 *
 * **It APPENDS**, which is why the cap counts past what the plan already holds — a cap checked
 * only on the payload is one fifty requests of ten lines walk straight past.
 *
 * **THE GRID ASKS FOR EVERYTHING A LINE RECORDS**, in one pass, and scrolls sideways to do it.
 * It once asked only for the required columns, with the window, the conference and the remarks
 * moved to a line's own editor so the seven that were left would fit the modal — and that cost
 * more than it saved: a department filing thirty lines opened thirty editors to set dates that
 * are usually the same across a batch, and the conference flag lost its only control, so the
 * column could never hold anything but `false`. Reversed 13 Oct 2026 (product call).
 *
 * The sideways scroll is the exception a FORM is not normally allowed. It is earned here by
 * this being a dense ENTRY TABLE rather than a form — and the required columns all sit left of
 * the fold, so nothing a line cannot be saved without has to be hunted for.
 */
export function PpmpLineItemsModal({
  open,
  ppmp,
  onClose,
  onSaved,
}: {
  open: boolean;
  ppmp: PpmpDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<DraftRow[]>([EMPTY()]);
  const [types, setTypes] = useState<ProcurementCatalogOption[]>([]);
  const [modes, setModes] = useState<ProcurementCatalogOption[]>([]);
  const [catalog, setCatalog] = useState<PpmpItemCatalogOption[]>([]);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    procurementCatalogsApi
      .options('types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setTypes(rows);
      })
      .catch(() => {
        // Fails soft — the save still refuses a retired or unknown catalog row.
      });

    procurementCatalogsApi
      .options('modes', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setModes(rows);
      })
      .catch(() => {});

    ppmpItemsApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCatalog(rows);
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  const typeOptions: SelectOption[] = types.map((t) => ({ value: t.id, label: t.name }));
  const modeOptions: SelectOption[] = modes.map((m) => ({ value: m.id, label: m.name }));

  const catalogOptions: SelectOption[] = catalog.map((c) => ({
    value: c.id,
    // The option SAYS WHAT IT IS — the specification's own words, not its id.
    label: c.itemDescriptionText,
    sublabel: c.unit,
    keywords: c.unit,
  }));

  function patch(key: string, next: Partial<DraftRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...next } : row)));
  }

  /**
   * Choosing a catalogue entry SNAPSHOTS its description, its unit and its cost onto the row.
   *
   * The catalog SUGGESTS and never constrains — the tail of what an organization buys is
   * unbounded — so the cells stay editable and the id is kept beside them to say where the
   * wording came from.
   */
  function chooseCatalogItem(key: string, next: string | null) {
    const chosen = catalog.find((c) => c.id === next);

    patch(key, {
      ppmpItemCatalogId: next,
      ...(chosen
        ? {
            descriptionHtml: chosen.itemDescription,
            descriptionText: chosen.itemDescriptionText,
            unit: chosen.unit,
            // The unit cost SUGGESTS the line's total and is not read again.
            estimatedBudget: chosen.estimatedUnitCost ?? null,
          }
        : {}),
    });
  }

  const filled = useMemo(() => rows.filter(isFilled), [rows]);
  const started = useMemo(() => rows.filter((row) => !isBlank(row)), [rows]);

  // Every STARTED row must be complete: a half-filled row is one somebody began, and losing it
  // silently is worse than refusing it. A wholly blank row is dropped by the server.
  const canSave = filled.length > 0 && filled.length === started.length && confirmed && !saving;

  const draftTotal = filled.reduce((sum, row) => sum + (row.estimatedBudget ?? 0), 0);

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    /*
      EVERY row is sent, blanks included, so the index a rejection names is the index of the
      row ON SCREEN. Dropping them here would shift every later row's number and point the
      reader at the wrong cell; the server drops them after validating.
    */
    const items: PpmpLineItemInput[] = rows.map((row) => ({
      ppmpItemCatalogId: row.ppmpItemCatalogId,
      itemDescription: row.descriptionHtml ?? row.descriptionText,
      procurementTypeId: row.procurementTypeId,
      modeOfProcurementId: row.modeOfProcurementId,
      quantity: row.quantity ?? 0,
      unit: row.unit.trim(),
      estimatedBudget: row.estimatedBudget ?? 0,
      preProcurementConference: row.preProcurementConference,
      procurementStartDate: row.procurementStartDate || null,
      procurementEndDate: row.procurementEndDate || null,
      expectedDeliveryDate: row.expectedDeliveryDate || null,
      remarks: row.remarks.trim() || null,
    }));

    try {
      await ppmpsApi.addLineItems(ppmp.id, { items });
      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not add the lines.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppmp-lines' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add procurement lines"
      size="3xl"
      id="ppmp-lines-modal"
      autogenerate={{
        /*
          Fills the LAST row — never one the reader has already decided — from the live
          catalogs, so the primary action enables on the first try with real ids the server
          will accept.
        */
        onGenerate: () => {
          setRows((current) => {
            const next = [...current];
            const index = next.length - 1;

            next[index] = { ...next[index], ...samplePpmpLine(types, modes, catalog) };

            return next;
          });
        },
        disabled: saving || types.length === 0 || modes.length === 0,
      }}
      confirm={{
        id: 'ppmp-lines-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppmp-lines-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppmp-lines-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The plan these lines are filed under, stated once — a read-only fact. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'PPMP', value: ppmp.ppmpNumber, mono: true },
            { label: 'Title', value: ppmp.title },
            { label: 'Lines already filed', value: String(ppmp.lineItems.length), mono: true },
          ]}
        />

        {types.length === 0 || modes.length === 0 ? (
          <p className="text-xs text-slate-500">
            A procurement line names what KIND of procurement it is and HOW it is procured, and
            both lists are empty. Add them under Procurement Planning → Settings first.
          </p>
        ) : null}

        <InlineAddTable
          /*
            EVERY FIELD A LINE HAS, IN ONE PASS — the reference's own shape, and the reversal of
            a narrower grid that fitted the modal by moving four fields to the line editor
            (13 Oct 2026, product call). That split cost more than it saved: a department filing
            thirty lines opened thirty editors to set dates that are usually the same across the
            batch, and Pre-proc conf. lost its control altogether, so the column could only ever
            hold `false`.

            SO THIS GRID SCROLLS SIDEWAYS, which a form is otherwise not allowed to do. It earns
            the exception by being a dense ENTRY TABLE rather than a form: the reader is filling
            a register row by row, the horizontal scroller was already there, and every required
            column sits left of the fold where nothing has to be hunted for.
          */
          headers={[
            'Catalogue',
            'Description *',
            'Type *',
            'Mode *',
            { label: 'Qty *', align: 'right' },
            'Unit *',
            { label: 'Est. budget', align: 'right' },
            'Pre-proc conf.',
            'Procurement from',
            'Procurement to',
            'Expected delivery',
            'Remarks',
          ]}
          colWidths={[150, 220, 130, 140, 70, 90, 130, 110, 140, 140, 140, 200]}
          /*
            VERSIONED with the column set — stored widths here are POSITIONAL, so a grid that
            changes its columns and keeps its key hands every reader who ever dragged one a
            broken layout and nobody else.
          */
          resizeKey="ppmp-lines.colWidths.v2"
          flexColumn={1}
          addButtonId="ppmp-lines-add"
          addDisabled={filled.length < started.length}
          addDisabledReason={
            filled.length < started.length
              ? 'Finish the row you have started before adding another.'
              : undefined
          }
          rowCount={rows.length}
          onAddRow={() => setRows((current) => [...current, EMPTY()])}
        >
          {rows.map((row, index) => (
            <InlineRow
              key={row.key}
              idx={index + 1}
              onRemove={() => setRows((current) => current.filter((x) => x.key !== row.key))}
            >
              <TdSelect
                id={cellId(index, 'ppmpItemCatalogId')}
                ariaLabel={`Catalogue item, row ${index + 1}`}
                value={row.ppmpItemCatalogId}
                onChange={(next) => chooseCatalogItem(row.key, next)}
                options={catalogOptions}
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'itemDescription')}
                ariaLabel={`Description, row ${index + 1}`}
                value={row.descriptionText}
                placeholder="What is being bought"
                /*
                  Typing DROPS the catalogue's markup: from here the typed words are what the
                  line says, and keeping the HTML would send something the reader cannot see.
                */
                onChange={(next) =>
                  patch(row.key, { descriptionText: next, descriptionHtml: null })
                }
                disabled={saving}
              />

              <TdSelect
                id={cellId(index, 'procurementTypeId')}
                ariaLabel={`Procurement type, row ${index + 1}`}
                value={row.procurementTypeId}
                onChange={(next) => patch(row.key, { procurementTypeId: next })}
                options={typeOptions}
                disabled={saving}
              />

              <TdSelect
                id={cellId(index, 'modeOfProcurementId')}
                ariaLabel={`Mode of procurement, row ${index + 1}`}
                value={row.modeOfProcurementId}
                onChange={(next) => patch(row.key, { modeOfProcurementId: next })}
                options={modeOptions}
                disabled={saving}
              />

              <TdNumber
                id={cellId(index, 'quantity')}
                ariaLabel={`Quantity, row ${index + 1}`}
                value={row.quantity}
                min={0}
                onChange={(next) => patch(row.key, { quantity: next })}
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'unit')}
                ariaLabel={`Unit, row ${index + 1}`}
                value={row.unit}
                placeholder="pc, box, lot"
                onChange={(next) => patch(row.key, { unit: next })}
                disabled={saving}
              />

              <TdMoney
                id={cellId(index, 'estimatedBudget')}
                ariaLabel={`Estimated budget, row ${index + 1}`}
                value={row.estimatedBudget}
                onChange={(next) => patch(row.key, { estimatedBudget: next })}
                disabled={saving}
              />

              <TdCheckbox
                id={cellId(index, 'preProcurementConference')}
                ariaLabel={`Pre-procurement conference, row ${index + 1}`}
                checked={row.preProcurementConference}
                onChange={(next) => patch(row.key, { preProcurementConference: next })}
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'procurementStartDate')}
                ariaLabel={`Procurement from, row ${index + 1}`}
                value={row.procurementStartDate}
                onChange={(next) => patch(row.key, { procurementStartDate: next })}
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'procurementEndDate')}
                ariaLabel={`Procurement to, row ${index + 1}`}
                value={row.procurementEndDate}
                onChange={(next) => patch(row.key, { procurementEndDate: next })}
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'expectedDeliveryDate')}
                ariaLabel={`Expected delivery, row ${index + 1}`}
                value={row.expectedDeliveryDate}
                onChange={(next) => patch(row.key, { expectedDeliveryDate: next })}
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'remarks')}
                ariaLabel={`Remarks, row ${index + 1}`}
                value={row.remarks}
                placeholder="Anything worth noting"
                onChange={(next) => patch(row.key, { remarks: next })}
                disabled={saving}
              />

            </InlineRow>
          ))}
        </InlineAddTable>

        {/*
          THE TOTAL, BELOW THE TABLE, as a Table Display View — a read-only fact being SHOWN
          rather than a value being asked for, which is the test separating the two treatments.
          It is the DRAFT's own arithmetic and stops the moment the batch is saved: from then
          on the profile reads the server's figure.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Lines to add', value: String(filled.length), mono: true },
            { label: 'Estimated budget', value: formatMoney(draftTotal), mono: true },
          ]}
        />

        <p className="text-xs text-slate-500">
          Everything a line records is on this row — scroll the grid sideways for the
          procurement window, the conference and any remarks. Only the starred columns are
          required.
        </p>
      </div>
    </Modal>
  );
}
